import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SharedService } from './sharedService';

const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

export interface TrackerIssue {
    id?: string;
    title?: string;
    description?: string;
    status?: string;
    priority?: string;
    reporter?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface TrackerSettings {
    /** Whether a submit key is set. */
    configured: boolean;
    /** The last four characters of the submit key, never the key. */
    hint: string | null;
    /** Whether a SEPARATE read-only key is set. */
    readConfigured: boolean;
    readHint: string | null;
    /** True when listing falls back to the submit key because no read key is set. */
    readUsesSubmitKey: boolean;
    baseUrl: string;
    /** Where the key in force came from: 'settings', 'environment', or null. */
    source: string | null;
    updatedAt?: string | null;
    updatedBy?: string | null;
}

/**
 * Everything goes through fusion-server, never to the tracker directly.
 *
 * The tracker authenticates with a long-lived API key. A browser holding it
 * hands it to every user of this application, who can then read the project's
 * issues and file into it for as long as the key lives -- including after their
 * Fusion account is revoked. So the key stays on the server and this service
 * talks to our own API.
 */
@Injectable()
export class IssueTrackerService {
    constructor(private http: HttpClient, private sharedService: SharedService) {}

    /** Closed issues are left out unless asked for; the server defaults the same way. */
    list(includeClosed = false): Observable<{ issues: TrackerIssue[] }> {
        const params = new HttpParams().set('includeClosed', String(includeClosed));
        return this.http.get<{ issues: TrackerIssue[] }>(
            this.sharedService.apiUrl('api/issue-tracker/issues'), { params });
    }

    create(title: string, description: string, priority = 'medium'): Observable<{ issue: TrackerIssue }> {
        // No reporter: the server takes it from the session. One sent from here
        // would let anyone file in somebody else's name.
        return this.http.post<{ issue: TrackerIssue }>(
            this.sharedService.apiUrl('api/issue-tracker/issues'),
            { title, description, priority }, httpOptions);
    }

    getSettings(): Observable<TrackerSettings> {
        return this.http.get<TrackerSettings>(this.sharedService.apiUrl('api/issue-tracker/settings'));
    }

    /**
     * An omitted apiKey means "leave the stored one alone" -- this screen cannot
     * read the key, so it cannot post it back, and saving a changed base URL
     * would otherwise wipe it. Clearing is explicit.
     */
    saveSettings(value: {
        apiKey?: string;
        readApiKey?: string;
        baseUrl?: string;
        clearKey?: boolean;
        clearReadKey?: boolean;
    }): Observable<TrackerSettings> {
        return this.http.put<TrackerSettings>(
            this.sharedService.apiUrl('api/issue-tracker/settings'), value, httpOptions);
    }
}
